// Admin controller API — talks to the UE FastAPI under /underwriting-engine/controller.
// Every call carries a fresh Keycloak bearer token; the backend verifies it and
// requires the "Admin" realm role.
import axios from "axios";
// @ts-ignore - JS module, no types
import UserService from "../../../services/UserService";
// eslint-disable-next-line @typescript-eslint/no-var-requires
const serverConfig = require("../../../config/server.js");

const BASE = `${serverConfig.api_base_url}underwriting-engine/controller`;

export type Segment = "PG" | "CORP" | "ALL";

export type FieldType = "number" | "boolean" | "text";

export interface Condition {
  field: string;
  op: string;
  // number -> number; boolean -> null (operator carries truth); text -> string;
  // text "is one of" -> string[] as stored by the backend (string in the editor).
  value: number | string | string[] | null;
}

export interface Guardrail {
  guardrail_id: number;
  slug: string | null;
  name: string;
  description: string | null;
  segment: Segment;
  conditions: Condition[];
  na_reason: string;
  is_active: boolean;
  is_system: boolean;
  sort_order: number;
  created_by: string | null;
  created_at: string;
  updated_by: string | null;
  updated_at: string;
}

export interface FieldMeta { key: string; label: string; section: string; type: FieldType; }
export interface OperatorMeta { symbol: string; label: string; }
export interface ControllerSchema {
  segments: Segment[];
  sections: string[];
  fields: FieldMeta[];
  operators: Record<string, OperatorMeta>;
  operators_by_type: Record<FieldType, string[]>;
}

export interface HistoryEntry {
  history_id: number;
  guardrail_id: number | null;
  slug: string | null;
  action: "create" | "update" | "enable" | "disable" | "delete";
  before: Partial<Guardrail> | null;
  after: Partial<Guardrail> | null;
  changed_by: string;
  changed_at: string;
  note: string | null;
}

export interface GuardrailInput {
  name: string;
  description?: string | null;
  segment: Segment;
  conditions: Condition[];
  na_reason: string;
  sort_order?: number;
}

async function authConfig() {
  const token = await UserService.ensureFreshToken();
  return { headers: { Authorization: `Bearer ${token}` } };
}

export async function fetchSchema(): Promise<ControllerSchema> {
  const { data } = await axios.get(`${BASE}/schema`, await authConfig());
  return data.data;
}

export async function fetchGuardrails(): Promise<Guardrail[]> {
  const { data } = await axios.get(`${BASE}/guardrails`, await authConfig());
  return data.data;
}

export async function fetchHistory(guardrailId?: number): Promise<HistoryEntry[]> {
  const q = guardrailId != null ? `?guardrail_id=${guardrailId}` : "";
  const { data } = await axios.get(`${BASE}/guardrails/history${q}`, await authConfig());
  return data.data;
}

export async function createGuardrail(payload: GuardrailInput): Promise<Guardrail> {
  const { data } = await axios.post(`${BASE}/guardrails`, payload, await authConfig());
  return data.data;
}

export async function updateGuardrail(id: number, payload: Partial<GuardrailInput>): Promise<Guardrail> {
  const { data } = await axios.put(`${BASE}/guardrails/${id}`, payload, await authConfig());
  return data.data;
}

export async function setGuardrailActive(id: number, isActive: boolean): Promise<Guardrail> {
  const { data } = await axios.patch(`${BASE}/guardrails/${id}/active`, { is_active: isActive }, await authConfig());
  return data.data;
}

export async function deleteGuardrail(id: number): Promise<void> {
  await axios.delete(`${BASE}/guardrails/${id}`, await authConfig());
}
